import { Document, Primitive, vec4, WebIO } from '@gltf-transform/core';
import { t } from 'i18next';
import {
  BufferGeometry,
  Color,
  Group,
  Line,
  LineSegments,
  Matrix4,
  Mesh,
  Object3D,
  Points,
  Quaternion,
  Vector3,
} from 'three';
import { Text } from 'troika-three-text';
import _ from 'underscore';

import { AsbuiltPoint } from '../../../../../typings/api/integrations/xsite/rest/v1/skymap-xsite-connection';
import { ProgressController } from '../../../react/utils/progress-controller';
import { PromiseAbortController } from '../../../react/utils/promise-abort-controller';
import { formattedDateTime } from '../../../utils/dateUtils';
import { isWireframe } from '../../../utils/three/mesh';
import { isShownBelow } from '../../../utils/three/visibility';
import { deepCopy, isDefined, mapToArray } from '../../../utils/variables';
import { CoordinateManager } from '../../coordinate-manager';
import { MeshPrimitiveMode } from '../../geodata/glb/types';
import { publish, SubscriptionTopic } from '../../messaging/pubsub';
import { DesignTool } from '../../tools/design-tool';
import { addAndCommitAction } from '../../tools/utils/context-menu/design-tool/utils/actions';
import {
  AttributeDataWithId,
  getAttributeDataOrAddValue,
  getAttributeOrAdd,
} from '../../tools/utils/context-menu/design-tool/utils/attributes';
import { repositionDesign } from '../../tools/utils/context-menu/design-tool/utils/re-position';
import { Point3d } from '../../utilities/math-helpers';
import { Design } from '../design';
import { ElementObj } from '../element-obj';
import { Geometry } from '../geometry';
import { Glb } from '../glb';
import { isStyledLines, StyledLines } from '../styled-lines/styled-lines';
import { getDefaultEnumValueColor } from './colors';
import {
  createIterator,
  DesignNode,
  DesignNodeMaterials,
  IdIterator,
  RevertChange,
} from './design-node/design-node';
import { getHiddenSeams } from './design-node/utils/circle';
import { getSegmentConic } from './design-node/utils/conic';
import { FaceId, FaceNode } from './design-node/utils/face-node';
import {
  addLineSegmentsToDesignNode,
  LineSegmentId,
  LineSegmentNode,
} from './design-node/utils/line-segment-node';
import { PointId, PointNode } from './design-node/utils/point-node';
import {
  AttributeData,
  AttributeGroupEntry,
  AttributeGroups,
  AttributeGroupsJson,
  AttributeId,
  ColorCoding,
  EnumValuesJson,
  extractSkyMapRootNodeAttributes,
  NumberValuesJson,
  SkyMapAttribute,
  SkyMapAttributeJson,
  SkyMapExtrasUnitType,
  SkyMapPositionPrimitiveExtrasJson,
  SkyMapRootNodeExtras,
  StringValueMetadata,
  StringValuesJson,
  StripeJson,
  ValueId,
} from './skymap-extras';

function convertSkymapAttributeToJsonAttribute(attribute: SkyMapAttribute): SkyMapAttributeJson {
  if (attribute.type === 'enum') {
    const values = [...attribute.valueMap];
    const colors = [...attribute.colorMap].map(
      (x) => [x[0], [x[1].r, x[1].g, x[1].b]] as [ValueId, [number, number, number]],
    );

    return {
      id: attribute.id,
      type: 'enum',
      name: attribute.name,
      source: attribute.source,
      colors,
      values,
    };
  }

  if (attribute.type === 'number') {
    return {
      id: attribute.id,
      type: 'number',
      name: attribute.name,
      source: attribute.source,
      unit: attribute.unit,
    };
  }

  return {
    id: attribute.id,
    type: 'string',
    name: attribute.name,
    display: attribute.display,
    source: attribute.source,
  };
}

function getPrimitiveExtras<
  TId extends PointId | LineSegmentId | FaceId,
  TNode extends PointNode | LineSegmentNode | FaceNode,
>(
  ids: TId[],
  getNode: (id: TId) => TNode,
  pushItem: (item: TNode, nextId: TId) => void,
  valueMaps: ValueMaps<TId>,
): SkyMapPositionPrimitiveExtrasJson<TId> {
  const extras: SkyMapPositionPrimitiveExtrasJson<TId> = {
    skymap: {
      enumAttributes: mapToArray(valueMaps.enumValues).map((x) => x[1]),
      stringAttributes: mapToArray(valueMaps.stringValues).map((x) => x[1]),
      numberAttributes: mapToArray(valueMaps.numberValues).map((x) => x[1]),
    },
  };

  const iterator = createIterator<TId>();
  for (const id of ids) {
    const node = getNode(id);
    const nextId = iterator.next();
    pushItem(node, nextId);

    for (const [attributeId, attributeData] of node.attributes.entries()) {
      if (attributeData.type === 'enum') {
        valueMaps.enumValues.get(attributeId)!.valueMap.push([nextId, attributeData.index]);
      } else if (attributeData.type === 'string') {
        const value: StringValueMetadata = {
          val: attributeData.value,
        };

        // Only assign if there is something to assign.
        if (isDefined(attributeData.height)) {
          value.H = attributeData.height;
        }
        if (isDefined(attributeData.rotation)) {
          value.R = attributeData.rotation;
        }

        valueMaps.stringValues.get(attributeId)!.valueMap.push([nextId, value]);
      } else if (attributeData.type === 'number') {
        valueMaps.numberValues.get(attributeId)!.valueMap.push([nextId, attributeData.value]);
      }
    }
  }

  return extras;
}

type ValueMaps<T extends PointId | LineSegmentId | FaceId> = {
  enumValues: Map<AttributeId, EnumValuesJson<T>>;
  stringValues: Map<AttributeId, StringValuesJson<T>>;
  numberValues: Map<AttributeId, NumberValuesJson<T>>;
};

function getValueMaps<T extends PointId | LineSegmentId | FaceId>(): ValueMaps<T> {
  return {
    enumValues: new Map<AttributeId, EnumValuesJson<T>>(),
    stringValues: new Map<AttributeId, StringValuesJson<T>>(),
    numberValues: new Map<AttributeId, NumberValuesJson<T>>(),
  };
}

function setAttribute(id: AttributeId, maps: ValueMaps<any>) {
  maps.enumValues.set(id, { id: id, valueMap: [] });
  maps.stringValues.set(id, { id: id, valueMap: [] });
  maps.numberValues.set(id, { id: id, valueMap: [] });
}

export function serializeDesignNodeToGlb(
  node: DesignNode,
  attributes: Map<AttributeId, SkyMapAttribute>,
  colorCoding: ColorCoding,
  options?: {
    skipColors?: boolean;
    groups?: AttributeGroups;
    quaternion?: Quaternion;
  },
): ArrayBuffer {
  const doc = new Document();
  const io = new WebIO();

  const pointMaps = getValueMaps<PointId>();
  const lineSegmentMaps = getValueMaps<LineSegmentId>();
  const faceMaps = getValueMaps<FaceId>();

  const jsonAttributes: SkyMapAttributeJson[] = [];
  for (const attribute of attributes.values()) {
    jsonAttributes.push(convertSkymapAttributeToJsonAttribute(attribute));

    setAttribute(attribute.id, pointMaps);
    setAttribute(attribute.id, lineSegmentMaps);
    setAttribute(attribute.id, faceMaps);
  }
  const jsonGroups: AttributeGroupsJson = [];
  if (isDefined(options?.groups)) {
    for (const group of options.groups.entries()) {
      const name = group[0];
      const data: [string, AttributeDataWithId[]][] = group[1].map((attributes) => {
        return [
          attributes.displayName,
          [...attributes.data.entries()].map((entry) => {
            return {
              id: entry[0],
              ...entry[1],
            };
          }),
        ];
      });

      jsonGroups.push([name, data]);
    }
  }

  const pointIdMap = new Map<PointId, PointId>();

  const getNewPointId = (id: PointId): PointId => {
    const newId = pointIdMap.get(id);
    if (!isDefined(newId)) {
      throw new Error('Error serializing design node. Point ID not found.');
    }
    return newId;
  };

  const pointIndices: number[] = [];
  const faceIndices: number[] = [];
  const lineSegmentIndices: number[] = [];

  const vertices: Vector3[] = [];
  const color: number[] = [];

  const colorAttribute =
    colorCoding.enabled && isDefined(colorCoding.attributeId)
      ? attributes.get(colorCoding.attributeId)
      : undefined;

  const pointPrimitiveExtras = getPrimitiveExtras<PointId, PointNode>(
    node.points,
    (id) => node.getPointById(id),
    (x, nextId) => {
      pointIdMap.set(x.id, nextId);
      pointIndices.push(nextId);
      vertices.push(x.vertex);

      const data = isDefined(colorAttribute) ? x.attributes.get(colorAttribute.id) : undefined;
      const rgb =
        data?.type === 'enum' && colorAttribute?.type === 'enum'
          ? colorAttribute.colorMap.get(data.index)?.toArray()!
          : [1, 1, 1];

      color.push(...rgb);
    },
    pointMaps,
  );

  // Line styles, see skymap-oda's shared/cpp/glb/README.md (Line styles).
  const curves: [LineSegmentId, [number, number, number], number][] = [];
  const widths: [LineSegmentId, number][] = [];
  const stripes: [LineSegmentId, StripeJson][] = [];

  const lineSegmentPrimitiveExtras = getPrimitiveExtras<LineSegmentId, LineSegmentNode>(
    node.lineSegments,
    (id) => node.getLineSegmentById(id),
    (segment, nextId) => {
      lineSegmentIndices.push(getNewPointId(segment.values[0]), getNewPointId(segment.values[1]));

      // The control point is in the coordinates of the positions.
      if (segment.curve) {
        const [start, end] = node.getVerticesByLineSegmentId(segment.id, 'local');
        const { control, weight } = getSegmentConic(start, end, segment.curve);
        curves.push([nextId, control.toArray(), weight]);
      }

      if (segment.width > 0) {
        widths.push([nextId, segment.width]);
      }

      if (segment.stripe) {
        const { pattern, scale, offset } = segment.stripe;
        stripes.push([nextId, { P: pattern, S: scale, O: offset }]);
      }
    },
    lineSegmentMaps,
  );

  if (curves.length > 0) {
    lineSegmentPrimitiveExtras.skymap.curves = curves;
  }

  if (widths.length > 0) {
    lineSegmentPrimitiveExtras.skymap.widths = widths;
  }

  if (stripes.length > 0) {
    lineSegmentPrimitiveExtras.skymap.stripes = stripes;
    lineSegmentPrimitiveExtras.skymap.stripePatterns = node.stripePatterns;
  }

  // The seams of the intact circles, with their center point, see circle.ts.
  const seams = [...getHiddenSeams(node)].map((id): [PointId, PointId] => [
    getNewPointId(id),
    getNewPointId(node.getPointById(id).seamOf!),
  ]);

  if (seams.length > 0) {
    lineSegmentPrimitiveExtras.skymap.seams = seams;
  }

  const facePrimitiveExtras = getPrimitiveExtras<FaceId, FaceNode>(
    node.faces,
    (id) => node.getFaceById(id),
    (node) => {
      faceIndices.push(
        getNewPointId(node.values[0]),
        getNewPointId(node.values[1]),
        getNewPointId(node.values[2]),
      );
    },
    faceMaps,
  );

  const baseScene = doc.createScene('Model');
  const mainBuffer = doc.createBuffer();

  const getIndexAccessor = (array: number[]) =>
    doc
      .createAccessor('INDICES')
      .setArray(new Uint32Array([...array]))
      .setType('SCALAR')
      .setBuffer(mainBuffer);

  const mesh = doc.createMesh('Mesh');

  if (pointIndices.length > 0) {
    // Create accessor for vertices
    const vertexAccessor = doc
      .createAccessor('VERTICES')
      .setArray(new Float32Array([...vertices.map((x) => x.toArray()).flat()]))
      .setType('VEC3')
      .setBuffer(mainBuffer);

    /**
     * TODO: SK-5707
     * There is a bug when importing the GLB into design where The wireframe of surfaces gets
     * serialized as individual line segments resulting in duplicate line segments.
     * The skipColors options can be removed once that bug is resolved.
     */
    const colorAccessor = options?.skipColors
      ? undefined
      : doc
          .createAccessor('COLOR')
          .setArray(new Float32Array(color))
          .setType('VEC3')
          .setBuffer(mainBuffer);

    const setColorAccessor = (primitive: Primitive) => {
      if (isDefined(colorAccessor)) {
        primitive.setAttribute('COLOR_0', colorAccessor);
      }
    };

    const pointsPrimitive = doc
      .createPrimitive()
      .setMode(MeshPrimitiveMode.POINTS)
      .setAttribute('POSITION', vertexAccessor)
      .setIndices(getIndexAccessor(pointIndices))
      .setExtras(pointPrimitiveExtras);

    mesh.addPrimitive(pointsPrimitive);
    setColorAccessor(pointsPrimitive);

    if (faceIndices.length > 0) {
      const facePrimitive = doc
        .createPrimitive()
        .setAttribute('POSITION', vertexAccessor)
        .setIndices(getIndexAccessor(faceIndices))
        .setExtras(facePrimitiveExtras);

      mesh.addPrimitive(facePrimitive);
      setColorAccessor(facePrimitive);
    }

    if (lineSegmentIndices.length > 0) {
      const lineSegmentPrimitive = doc
        .createPrimitive()
        .setMode(MeshPrimitiveMode.LINES)
        .setAttribute('POSITION', vertexAccessor)
        .setIndices(getIndexAccessor(lineSegmentIndices))
        .setExtras(lineSegmentPrimitiveExtras);

      mesh.addPrimitive(lineSegmentPrimitive);
      setColorAccessor(lineSegmentPrimitive);
    }
  }

  const skymapExtras: SkyMapRootNodeExtras = {
    skymap: {
      attributes: jsonAttributes,
      version: '2',
      colorCoding,
      groups: jsonGroups,
    },
  };

  const rootNode = doc
    .createNode('Node')
    .setMesh(mesh)
    .setTranslation(node.group.position.toArray());

  if (isDefined(options?.quaternion)) {
    rootNode.setRotation(options.quaternion.toArray() as vec4);
  }

  baseScene.addChild(rootNode).setExtras(skymapExtras);

  return io.writeBinary(doc);
}

export function generateDesignNodeFromAsbuiltPoints(
  asbuiltPoints: AsbuiltPoint[],
  offset: Point3d = { x: 0, y: 0, z: 0 },
): {
  attributes: Map<AttributeId, SkyMapAttribute>;
  node: DesignNode;
} {
  const attributes = new Map<AttributeId, SkyMapAttribute>();
  const attributeMap = new Map<keyof AsbuiltPoint, AttributeId>();
  const attributeEnumValueMap = new Map<
    AttributeId,
    { iterator: IdIterator<ValueId>; map: Map<string, ValueId> }
  >();
  const lines = new Map<ValueId, PointNode[]>();

  const iterator = createIterator<AttributeId>();

  const createEnumAttribute = (id: AttributeId, name: string, key: keyof AsbuiltPoint) => {
    attributes.set(id, {
      type: 'enum',
      source: 'as-built',
      name: name,
      valueMap: new Map<ValueId, string>(),
      colorMap: new Map<ValueId, Color>(),
      id,
    });

    attributeMap.set(key, id);
    attributeEnumValueMap.set(id, { iterator: createIterator<ValueId>(), map: new Map() });
  };

  const createValueAttribute = (
    id: AttributeId,
    name: string,
    key: keyof AsbuiltPoint,
    unitType?: SkyMapExtrasUnitType,
  ) => {
    attributes.set(id, {
      type: 'number',
      source: 'as-built',
      name: name,
      unit: unitType,
      id,
    });

    attributeMap.set(key, id);
  };

  const categoryAttributeId = iterator.next();
  createEnumAttribute(
    categoryAttributeId,
    t('asBuiltColumns.category', { ns: 'skyviewAsbuilt' }) as string,
    'category',
  );

  const nameAttributeId = iterator.next();
  createEnumAttribute(
    nameAttributeId,
    t('asBuiltColumns.name', { ns: 'skyviewAsbuilt' }) as string,
    'name',
  );

  const lineAttributeId = iterator.next();
  createEnumAttribute(
    lineAttributeId,
    t('asBuiltColumns.lineId', { ns: 'skyviewAsbuilt' }) as string,
    'lineId',
  );

  createEnumAttribute(
    iterator.next(),
    t('asBuiltColumns.fileReference', {
      ns: 'skyviewAsbuilt',
    }) as string,
    'fileReference',
  );
  createEnumAttribute(
    iterator.next(),
    t('asBuiltColumns.measureType', { ns: 'skyviewAsbuilt' }) as string,
    'measureType',
  );
  createEnumAttribute(
    iterator.next(),
    t('asBuiltColumns.machineId', { ns: 'skyviewAsbuilt' }) as string,
    'machineId',
  );
  createEnumAttribute(
    iterator.next(),
    t('asBuiltColumns.machineName', { ns: 'skyviewAsbuilt' }) as string,
    'machineName',
  );
  createEnumAttribute(
    iterator.next(),
    t('asBuiltColumns.description', { ns: 'skyviewAsbuilt' }) as string,
    'description',
  );

  createValueAttribute(
    iterator.next(),
    t('asBuiltColumns.localId', { ns: 'skyviewAsbuilt' }) as string,
    'localId',
  );
  createValueAttribute(
    iterator.next(),
    t('asBuiltColumns.timestamp', { ns: 'skyviewAsbuilt' }) as string,
    'timestampMs',
    'timestamp',
  );

  const originId = iterator.next();
  const xSiteValueId = 0 as ValueId;
  const originAttribute: SkyMapAttribute = {
    type: 'enum',
    source: 'as-built',
    name: t('asBuiltColumns.origin', { ns: 'skyviewAsbuilt' }),
    valueMap: new Map<ValueId, string>(),
    colorMap: new Map<ValueId, Color>(),
    id: originId,
  };
  originAttribute.valueMap.set(xSiteValueId, 'Xsite');
  originAttribute.colorMap.set(xSiteValueId, getDefaultEnumValueColor(xSiteValueId));

  attributes.set(originId, originAttribute);

  const getAttributeData = (p: AsbuiltPoint) => {
    const map = new Map<AttributeId, AttributeData>();

    const getId = (key: keyof AsbuiltPoint) => {
      const id = attributeMap.get(key);
      if (!isDefined(id)) {
        throw new Error('Error generating design node. Attribute ID not found.');
      }
      return id;
    };

    const getValueId = (id: AttributeId, value: string) => {
      const attribute = attributes.get(id);
      if (!isDefined(attribute) || attribute.type !== 'enum') {
        throw new Error('Error generating design node. Attribute not found.');
      }

      const enumValues = attributeEnumValueMap.get(id);
      if (!isDefined(enumValues)) {
        throw new Error('Error generating design node. Enum values not found.');
      }

      let valueId = enumValues.map.get(value);
      if (!isDefined(valueId)) {
        valueId = enumValues.iterator.next();
        enumValues.map.set(value, valueId);
        attribute.valueMap.set(valueId, value);

        // Set a random color based on the valueId.
        attribute.colorMap.set(valueId, getDefaultEnumValueColor(valueId));
      }

      return valueId;
    };

    if (isDefined(p.category)) {
      const id = getId('category');
      map.set(id, { type: 'enum', index: getValueId(id, p.category) });
    }
    if (isDefined(p.measureType)) {
      const id = getId('measureType');
      map.set(id, { type: 'enum', index: getValueId(id, p.measureType) });
    }
    if (isDefined(p.name)) {
      const id = getId('name');
      map.set(id, { type: 'enum', index: getValueId(id, p.name) });
    }
    if (isDefined(p.fileReference)) {
      const id = getId('fileReference');
      map.set(id, { type: 'enum', index: getValueId(id, p.fileReference) });
    }
    if (isDefined(p.lineId)) {
      const id = getId('lineId');
      map.set(id, { type: 'enum', index: getValueId(id, p.lineId.toString()) });
    }
    if (isDefined(p.machineId)) {
      const id = getId('machineId');
      map.set(id, { type: 'enum', index: getValueId(id, p.machineId) });
    }
    if (isDefined(p.machineName)) {
      const id = getId('machineName');
      map.set(id, { type: 'enum', index: getValueId(id, p.machineName) });
    }
    if (isDefined(p.description)) {
      const id = getId('description');
      map.set(id, { type: 'enum', index: getValueId(id, p.description) });
    }
    if (isDefined(p.localId)) {
      const id = getId('localId');
      map.set(id, { type: 'number', value: p.localId });
    }
    if (isDefined(p.timestampMs)) {
      map.set(getId('timestampMs'), { type: 'number', value: p.timestampMs });
    }

    map.set(originId, { type: 'enum', index: xSiteValueId });

    return map;
  };

  const node = new DesignNode([]);
  for (const point of asbuiltPoints) {
    if (!isDefined(point.x) || !isDefined(point.y) || !isDefined(point.z)) {
      continue;
    }

    // X and Y are flipped in the asbuilt data.
    // Subtract the offset of the data to get the local vertex position.
    const position = new Vector3(point.y - offset.y, point.x - offset.x, point.z - offset.z);

    const result = node.addPoint(position, getAttributeData(point));

    if (isDefined(point.lineId)) {
      const enumValues = attributeEnumValueMap.get(lineAttributeId);
      if (!isDefined(enumValues)) {
        throw new Error('Error creating lines. Enum values not found.');
      }

      const valueId = enumValues.map.get(point.lineId.toString());
      if (!isDefined(valueId)) {
        throw new Error('Error creating lines. Value ID not found.');
      }

      const pointArray = lines.get(valueId) ?? [];
      pointArray.push(node.getPointById(result.id));
      lines.set(valueId, pointArray);
    }
  }

  // Group by line id and add line segments.
  for (const entry of lines.entries()) {
    const lineAttributes = new Map<AttributeId, AttributeData>();
    lineAttributes.set(lineAttributeId, { type: 'enum', index: entry[0] });

    const nameValueId = getAttributeValueIdForLine(entry[1], nameAttributeId);
    if (isDefined(nameValueId)) {
      lineAttributes.set(nameAttributeId, { type: 'enum', index: nameValueId });
    }

    const categoryValueId = getAttributeValueIdForLine(entry[1], categoryAttributeId);
    if (isDefined(categoryValueId)) {
      lineAttributes.set(categoryAttributeId, { type: 'enum', index: categoryValueId });
    }

    addLineSegmentsToDesignNode(
      node,
      entry[1]
        .map((_, idx) => {
          return idx === entry[1].length - 1 ? [] : [entry[1][idx].id, entry[1][idx + 1].id];
        })
        .flat(),
      lineAttributes,
    );
  }

  return { attributes, node };
}

export type GenerateDesignNodesResult = {
  nodes: DesignNode[];
  transformationMatrix: Matrix4;
};

export function generateDesignNodesFromAsset(asset: ElementObj): GenerateDesignNodesResult {
  if (!asset.getStateVisibility()) {
    throw new Error(
      'Unable to generate design nodes from asset. The asset is not visible in the scene.',
    );
  }

  /**
   * This ensures that polylines are connected when imported from LandXml.
   * This may be expanded when encountering other filetypes that are imported
   * in unexpected ways.
   */
  const mergeDuplicatePoints =
    (asset instanceof Glb && asset.getFileType() === 'LandXml') || asset instanceof Geometry;

  return asset instanceof Design
    ? asset.getDesignNodeData()
    : generateDesignNodes(asset, { mergeDuplicatePoints });
}

export function generateDesignNodes(
  scene: Group | Object3D,
  options?: {
    materials?: DesignNodeMaterials;
    mergeDuplicatePoints?: boolean;
    /**
     * When loading a persisted design, the asset rotation is stored on the node while the
     * vertices are kept in their unrotated local frame (see serializeDesignNodeToGlb).
     * When set, the node rotation is neutralized before the DesignNode extracts the vertices
     * (which happens via localToWorld) and is instead returned on designNode.rotation.
     * Otherwise the rotation would be baked into the vertices here and then applied a second
     * time when the caller assigns it to the asset, doubling the rotation.
     */
    extractRootRotation?: boolean;
  },
): GenerateDesignNodesResult {
  const designNodes: DesignNode[] = [];

  const extras = extractSkyMapRootNodeAttributes(scene);

  const getData = (node: Object3D) => {
    return node.children.filter((x) => {
      // Text, and what is hidden, e.g. on a hidden layer of a drawing.
      if (x instanceof Text || !isShownBelow(x, scene)) {
        return false;
      }

      // Styled lines are meshes too, they are added with their lines, curves and fills.
      if (isStyledLines(x)) {
        return true;
      }

      if (
        x instanceof Points ||
        x instanceof LineSegments ||
        x instanceof Mesh ||
        x instanceof Line
      ) {
        return x.geometry instanceof BufferGeometry && !isWireframe(x);
      }

      return false;
    }) as (Points | LineSegments | Mesh | Line | StyledLines)[];
  };

  scene.traverse((node) => {
    const data = getData(node);

    if (data.length > 0) {
      const rotation = node.rotation.clone();

      if (options?.extractRootRotation) {
        // Remove the rotation before the DesignNode extracts the vertices so it isn't
        // baked into the vertex positions (via localToWorld) in addition to being applied
        // to the asset by the caller.
        node.rotation.set(0, 0, 0);
        node.updateMatrixWorld(true);
      }

      const designNode = new DesignNode(data, options);
      designNode.rotation = rotation;
      designNodes.push(designNode);
      designNode.colorCoding = extras.colorCoding;
      designNode.attributes = extras.attributes;
      designNode.attributeGroups = extras.groups;
    }
  });

  if (designNodes.length === 0) {
    throw new Error('Unable to load design. No nodes found.');
  }

  return {
    nodes: designNodes,
    transformationMatrix: new Matrix4(),
  };
}

export async function appendResultToDesignNode(
  tool: DesignTool,
  result: GenerateDesignNodesResult,
  progressController: ProgressController,
  abortController: PromiseAbortController,
) {
  if (result.nodes.length === 0) {
    return;
  }

  const changes: RevertChange[] = [];
  const attributeIdMap = new Map<AttributeId, AttributeId>();
  const attributeIdValueMap = new Map<AttributeId, Map<ValueId, ValueId>>();

  // add attributes
  for (const [oldAttributeId, oldAttribute] of result.nodes[0].attributes) {
    const newAttributeResult = getAttributeOrAdd(tool, oldAttribute);
    const newAttributeId = newAttributeResult.attribute.id;
    attributeIdMap.set(oldAttributeId, newAttributeId);

    if (isDefined(newAttributeResult.change)) {
      changes.unshift(newAttributeResult.change);
    }

    if (oldAttribute.type === 'enum') {
      for (const [oldValueId, value] of oldAttribute.valueMap) {
        const newEnumValueChange = getAttributeDataOrAddValue(
          newAttributeResult.attribute,
          value,
          oldAttribute.colorMap.get(oldValueId),
        );

        if (isDefined(newEnumValueChange) && newEnumValueChange.attributeData.type === 'enum') {
          const newValueId = newEnumValueChange.attributeData.index;
          if (!attributeIdValueMap.has(newAttributeId)) {
            attributeIdValueMap.set(newAttributeId, new Map());
          }

          attributeIdValueMap.get(newAttributeId)!.set(oldValueId, newValueId);

          if (isDefined(newEnumValueChange.change)) {
            changes.unshift(newEnumValueChange.change);
          }
        }
      }
    }
  }

  const groups = tool.asset.designNode.getAttributeGroups();
  // add attribute groups (re-map the attribute id:s and value ids where necessary)
  for (const [oldName, entries] of result.nodes[0].attributeGroups) {
    let newName = oldName;
    // Rename group if another group with same name already exists.
    if (groups.find((x) => x.name === oldName)) {
      newName = t('section.functions.import.content.newNameOnNameCollision', {
        ns: 'skyviewDesign',
        oldName,
        dateTime: formattedDateTime(Date.now()),
      });
    }

    const newEntries: AttributeGroupEntry[] = [];
    for (const entry of entries) {
      const newEntry: AttributeGroupEntry = {
        data: new Map(),
        displayName: entry.displayName,
      };

      for (const [oldId, oldData] of entry.data) {
        const newData: AttributeData =
          oldData.type === 'enum'
            ? {
                type: 'enum',
                index: attributeIdValueMap.get(attributeIdMap.get(oldId)!)!.get(oldData.index)!,
              }
            : deepCopy(oldData);

        newEntry.data.set(attributeIdMap.get(oldId)!, newData);
      }

      newEntries.push(newEntry);
    }

    changes.unshift(tool.asset.designNode.addAttributeGroup(newName, newEntries));
  }

  const existingNode = tool.asset.designNode;

  let assetPosition = tool.asset.position.clone();

  if (existingNode.pointNodeCount === 0 && result.nodes[0].pointNodeCount > 0) {
    assetPosition = result.nodes[0]
      .getPointById(result.nodes[0].points[0])
      .vertex.clone()
      .setZ(tool.asset.position.z);

    const coordinates = CoordinateManager.getInstance().localToProjectCoords(assetPosition);
    changes.unshift(repositionDesign(tool, coordinates.easting, coordinates.northing));
  }

  const callbacks = [];
  for (const other of result.nodes) {
    const pointIdMap = new Map<PointId, PointId>();
    // The stripe patterns of the other node are added after the existing ones.
    let patternOffset = 0;

    callbacks.push(() => {
      patternOffset = existingNode.stripePatterns.length;
      const count = other.stripePatterns.length;
      existingNode.stripePatterns.push(...other.stripePatterns);
      changes.unshift({
        type: 'custom',
        callback: () => existingNode.stripePatterns.splice(patternOffset, count),
      });
    });

    for (const id of other.points) {
      callbacks.push(() =>
        appendPoint(
          existingNode,
          other,
          pointIdMap,
          changes,
          id,
          result.transformationMatrix,
          tool.asset.matrix,
          attributeIdMap,
          attributeIdValueMap,
        ),
      );
    }

    // The seams of circles, once their center points are added.
    callbacks.push(() => {
      for (const id of other.points) {
        const center = other.getPointById(id).seamOf;

        if (isDefined(center)) {
          changes.unshift(
            existingNode.upsertPoint({ id: pointIdMap.get(id)!, seamOf: pointIdMap.get(center) }),
          );
        }
      }
    });

    for (const id of other.lineSegments) {
      callbacks.push(() =>
        appendLineSegment(
          existingNode,
          other,
          pointIdMap,
          changes,
          id,
          attributeIdMap,
          attributeIdValueMap,
          patternOffset,
        ),
      );
    }

    for (const id of other.faces) {
      callbacks.push(() =>
        appendFace(
          existingNode,
          other,
          pointIdMap,
          changes,
          id,
          attributeIdMap,
          attributeIdValueMap,
        ),
      );
    }
  }

  if (
    !tool.asset.designNode.colorCoding.enabled &&
    result.nodes[0].colorCoding.enabled &&
    isDefined(result.nodes[0].colorCoding.attributeId)
  ) {
    const enabled = tool.asset.designNode.colorCoding.enabled;
    const attributeId = tool.asset.designNode.colorCoding.attributeId;

    tool.asset.setColorCoding({
      enabled: true,
      attributeId: attributeIdMap.get(result.nodes[0].colorCoding.attributeId),
    });

    changes.unshift({
      type: 'custom',
      callback: () => {
        tool.asset.setColorCoding({
          enabled,
          attributeId,
        });
      },
    });
  }

  for (let i = 0; i < callbacks.length; i++) {
    callbacks[i]();
    if (i % 2000 === 0) {
      if (abortController.signal.aborted) {
        tool.asset.designNode.revert(changes);
        tool.asset.designNode.refresh();
        tool.asset.refreshMetadata();
        return;
      }

      progressController.callback(
        i / callbacks.length,
        t('section.functions.import.content.importingGeometryToDesign', {
          ns: 'skyviewDesign',
        }) as string,
      );
      await new Promise((resolve) => setTimeout(resolve, 1));
    }
  }

  tool.asset.designNode.refresh();
  tool.asset.refreshMetadata();
  publish(SubscriptionTopic.DesignAttributesChanged, undefined);

  addAndCommitAction(tool, {
    commit: () => {
      /**
       * Todo. In order to support redoing we need to implement so that progress bar can appear anytime when design tool is used.
       * For now we clear the redo-list when undone. and the action is not possiblet o re-do.
       */
    },
    rollback: () => {
      tool.asset.designNode.revert(changes);
      tool.asset.designNode.refresh();
      tool.asset.refreshMetadata();
      publish(SubscriptionTopic.DesignAttributesChanged, undefined);
      tool.undoActions = [];
    },
  });
}

function getNewAttributes(
  node: PointNode | FaceNode | LineSegmentNode,
  attributeIdMap: Map<AttributeId, AttributeId>,
  attributeIdValueMap: Map<AttributeId, Map<ValueId, ValueId>>,
) {
  const newAttributes = new Map<AttributeId, AttributeData>();
  for (const [oldId, oldData] of node.attributes) {
    const newData = deepCopy(oldData);
    if (newData.type === 'enum') {
      const newId = attributeIdMap.get(oldId)!;
      const valueMap = attributeIdValueMap.get(newId)!;
      newData.index = valueMap.get(newData.index)!;
    }
    newAttributes.set(attributeIdMap.get(oldId)!, newData);
  }
  return newAttributes;
}

function appendPoint(
  existingNode: DesignNode,
  other: DesignNode,
  pointIdMap: Map<PointId, PointId>,
  changes: RevertChange[],
  id: PointId,
  transformationMatrix: Matrix4,
  targetAssetMatrix: Matrix4,
  attributeIdMap: Map<AttributeId, AttributeId>,
  attributeIdValueMap: Map<AttributeId, Map<ValueId, ValueId>>,
) {
  const node = other.getPointById(id);

  // Apply the transformationMatrix first
  const transformedVertex = node.vertex.clone().applyMatrix4(transformationMatrix);

  // Apply the inverse of the targetAssetMatrix to correctly adjust for its transformation
  const targetInverseMatrix = new Matrix4().copy(targetAssetMatrix).invert();
  transformedVertex.applyMatrix4(targetInverseMatrix);

  const change = existingNode.addPoint(
    transformedVertex,
    getNewAttributes(node, attributeIdMap, attributeIdValueMap),
  );

  changes.unshift(change);
  pointIdMap.set(node.id, change.id);
}

function appendLineSegment(
  existingNode: DesignNode,
  other: DesignNode,
  pointIdMap: Map<PointId, PointId>,
  changes: RevertChange[],
  id: LineSegmentId,
  attributeIdMap: Map<AttributeId, AttributeId>,
  attributeIdValueMap: Map<AttributeId, Map<ValueId, ValueId>>,
  patternOffset: number,
) {
  const node = other.getLineSegmentById(id);
  changes.unshift(
    existingNode.addLineSegment(
      pointIdMap.get(node.values[0])!,
      pointIdMap.get(node.values[1])!,
      getNewAttributes(node, attributeIdMap, attributeIdValueMap),
      node.curve,
      {
        width: node.width,
        stripe: node.stripe && { ...node.stripe, pattern: node.stripe.pattern + patternOffset },
      },
    ),
  );
}

function appendFace(
  existingNode: DesignNode,
  other: DesignNode,
  pointIdMap: Map<PointId, PointId>,
  changes: RevertChange[],
  id: FaceId,
  attributeIdMap: Map<AttributeId, AttributeId>,
  attributeIdValueMap: Map<AttributeId, Map<ValueId, ValueId>>,
) {
  const node = other.getFaceById(id);
  const a = pointIdMap.get(node.values[0])!;
  const b = pointIdMap.get(node.values[1])!;
  const c = pointIdMap.get(node.values[2])!;

  changes.unshift(
    existingNode.addFace(a, b, c, getNewAttributes(node, attributeIdMap, attributeIdValueMap)),
  );

  if (!existingNode.findLineSegmentByPointIds(a, b)) {
    changes.unshift(existingNode.addLineSegment(a, b));
  }
  if (!existingNode.findLineSegmentByPointIds(a, c)) {
    changes.unshift(existingNode.addLineSegment(a, c));
  }
  if (!existingNode.findLineSegmentByPointIds(b, c)) {
    changes.unshift(existingNode.addLineSegment(b, c));
  }
}

/**
 * Gets the name attribute for the line.
 * If there is a mixture of ids, then return undefined.
 */
function getAttributeValueIdForLine(points: PointNode[], nameAttributeId: AttributeId) {
  const valueIds: (ValueId | undefined)[] = _.uniq(
    points
      .map((x) => x.attributes.get(nameAttributeId) as Extract<AttributeData, { type: 'enum' }>)
      .map((x) => x?.index),
  );

  return valueIds.length === 1 && isDefined(valueIds[0]) ? valueIds[0] : undefined;
}
